/**
 * Downloads a file by path. Use this instead of `window.location.href` or other mechanisms
 * as they might break an active lock. That happens as the URL temporarily changes and the
 * browser will show a confirmation dialog on whether you actually want to leave.
 */

import {showSnackbar, SnackbarType} from '@ELib/functions/snackbar';
import {TFunction} from '@ELib/utilities/i18next';

const FILENAME_REGEX =
  /filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/giu;

// export for testing
export const getFilename = (
  contentDisposition: string | null
): string | null => {
  let lastMatch = null;

  if (contentDisposition && contentDisposition.includes('attachment')) {
    let matches;

    while ((matches = FILENAME_REGEX.exec(contentDisposition)) !== null) {
      // This is necessary to avoid infinite loops with zero-width matches
      if (matches.index === FILENAME_REGEX.lastIndex) {
        FILENAME_REGEX.lastIndex++;
      }
      lastMatch = matches[matches.length - 1];
    }
  }

  return lastMatch ? decodeURI(lastMatch) : null;
};

// with error handling, but downloads file first, suited for smaller files
export const downloadGeneratedFile = async (
  url: string,
  t: TFunction
): Promise<void> => {
  const response = await fetch(url);
  if (response.ok) {
    const blob = await response.blob();
    const filename = getFilename(response.headers.get('Content-Disposition'));

    const link = document.createElement('a');
    link.setAttribute('download', '');
    link.href = URL.createObjectURL(blob);
    if (filename) {
      link.download = filename;
    }

    link.click();
  } else {
    showSnackbar({
      text: t('lib:errors.fileDownloadFailed'),
      type: SnackbarType.WARNING,
    });
  }
};

// no error handling but, initiates download immediately, e.g. for large files
export const downloadStaticFile = (filePath: string): void => {
  // taken from https://stackoverflow.com/a/43523297
  const link = document.createElement('a');

  link.setAttribute('download', '');
  link.href = filePath;
  link.click();
};
